import React, { useCallback, useContext, useState } from 'react';
import { useRouter } from 'next/router';
import { Product } from '@commercetools/frontend-domain-types/product/Product';
import { Transition } from '@headlessui/react';
import { XMarkIcon as CloseIcon } from '@heroicons/react/24/solid';
import { Link, Overlay } from '@loomstateorg/shared-frontend';
import { CurrencyHelpers } from '@loomstateorg/shared-frontend/helpers/currencyHelpers';
import { useFormat } from '@loomstateorg/shared-frontend/hooks/useFormat';
import useScrollBlock from '@loomstateorg/shared-frontend/hooks/useScrollBlock';
import useTouchDevice from '@loomstateorg/shared-frontend/hooks/useTouchDevice';
import Image from 'frontastic/lib/image';
import { AddToCartOverlayContextShape } from './types';
import { LineItem } from 'types/cart';

const AddToCartOverlayContext = React.createContext<AddToCartOverlayContextShape>({
  show() {},
  hide() {},
});

const AddToCartOverlayProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const router = useRouter();

  const { isTouchDevice } = useTouchDevice();

  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });
  const { formatMessage: formatCartMessage } = useFormat({ name: 'cart' });

  const { blockScroll } = useScrollBlock();

  const [lineItemByProduct, setLineItemByProduct] = useState<unknown[]>([]);
  const [discardedLineItems, setDiscardedLineItems] = useState<unknown[]>([]);

  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);

  const getLineItemByProduct = (products: Product[], lineItems: LineItem[]) => {
    return products
      .map((product) => {
        const prodAdded = { id: product.productId, name: product.name, lineItems: [] as LineItem[], totalPrice: 0 };
        const lineItemsByProduct = lineItems.filter((li) => product.variants.find((v) => v.sku === li.variant?.sku));
        if (lineItemsByProduct.length) {
          prodAdded.lineItems.push(...lineItemsByProduct);
          prodAdded.totalPrice = lineItemsByProduct
            ?.map((li) => (li?.count || 0) * (li?.variant?.price?.centAmount || 0))
            ?.reduce((acc, current) => (acc += current));
          return prodAdded;
        }
      })
      .filter((p) => p);
  };

  const showTitle = () => {
    if (lineItemByProduct?.length > 0) {
      return formatProductMessage({ id: 'cart.added', defaultMessage: 'Added to Cart' });
    } else if (lineItemByProduct?.length <= 0 && discardedLineItems.length > 0) {
      return formatProductMessage({ id: 'items.not.added', defaultMessage: 'Items not added' });
    }
  };

  const show = useCallback(
    (products: Product[], lineItems: LineItem[], discardedItems: LineItem[]) => {
      if (products.length && lineItems.length) {
        setLineItemByProduct(getLineItemByProduct(products, lineItems));
      }
      if (products.length && discardedItems) {
        setDiscardedLineItems(getLineItemByProduct(products, discardedItems));
      }
      blockScroll(true);
    },
    [blockScroll],
  );

  const hide = useCallback(() => {
    setLineItemByProduct([]);
    setDiscardedLineItems([]);
    blockScroll(false);
  }, [blockScroll]);

  return (
    <>
      {lineItemByProduct?.length > 0 && <Overlay showSpinner={false} onClick={hide} />}
      <Transition
        data-testid="add-to-cart-overlay"
        show={!!lineItemByProduct?.length || !!discardedLineItems?.length}
        className="fixed bottom-0 z-[9999] w-full overflow-hidden rounded-[20px_20px_0_0] bg-site shadow md:bottom-[unset] md:left-1/2 md:top-1/2 md:w-[90%] md:max-w-[650px] md:-translate-x-1/2 md:-translate-y-1/2 md:rounded-lg"
        enter="transition md:transition-opacity duration-75"
        enterFrom="opacity-0 translate-y-full"
        enterTo="opacity-100 translate-y-0"
        leave="transition md:transition-opacity duration-150"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-full"
      >
        <div onMouseUp={(e) => e.stopPropagation()}>
          <div className="p-16 md:px-48 md:py-24">
            <p className="text-18 leading-[27px] lg:text-22 lg:leading-[33px]" data-testid="title">
              {showTitle()}
            </p>

            <CloseIcon
              data-testid="close"
              aria-label="Close"
              className="absolute right-[18px] top-[16px] h-28 w-28 cursor-pointer fill-primary-regular stroke-0 md:top-[16px]"
              onClick={hide}
            />

            <div className="mt-22 md:mt-35 lg:mt-30">
              {lineItemByProduct?.length > 0 &&
                lineItemByProduct.map((p: any, index: number) => {
                  return (
                    <div className="flex items-center gap-24" key={p.id} data-testid={`added-item-${index}`}>
                      <div data-component-group="image-small" className="shrink-0 overflow-hidden">
                        <Image
                          width={60}
                          height={60}
                          src={p.lineItems?.[0].variant?.images?.[0] ?? '#'}
                          alt="Product image"
                          style={{ objectFit: 'contain' }}
                        />
                      </div>
                      <div className="flex grow items-start justify-between overflow-hidden">
                        <div className="max-w-full overflow-hidden">
                          <span
                            className="block max-w-full truncate font-primaryThin text-14 leading-normal"
                            data-testid={`added-item-name-${index}`}
                          >
                            {p?.name}
                          </span>
                          <span
                            className="mt-12 block font-primaryThin text-14 leading-normal"
                            data-testid={`added-item-quantity-${index}`}
                          >
                            {p.lineItems
                              ?.map((li: any) => `${li.count} - ${li.variant?.attributes?.size.key}`)
                              ?.join(', ')}
                          </span>
                        </div>
                        <span
                          className="hidden font-primaryThin text-16 leading-normal  md:block"
                          data-testid={`added-item-price-${index}`}
                        >
                          {CurrencyHelpers.formatForCurrency(p?.totalPrice ?? {}, router.locale)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              {discardedLineItems?.length > 0 &&
                discardedLineItems.map((p: any, index: number) => {
                  return (
                    <>
                      <span
                        className="mt-20 block max-w-full truncate font-primaryThin text-14 leading-normal"
                        data-testid={`not-added-title-${index}`}
                      >
                        {formatProductMessage({ id: 'items.not.added', defaultMessage: 'Items not added' })}
                      </span>
                      <span
                        className="mb-5 block max-w-full truncate font-primaryThin text-12 leading-normal"
                        data-testid={`not-added-disclaimer-${index}`}
                      >
                        {formatProductMessage({
                          id: 'items.not.added.disclaimer',
                          defaultMessage:
                            'Disclaimer: This items where not added since you have reached max quantity in your cart for this purchase.',
                        })}
                      </span>
                      <div className="flex items-center gap-24" key={p.id} data-testid={`not-added-item-${index}`}>
                        <div className="shrink-0">
                          <Image
                            width={60}
                            height={60}
                            src={p.lineItems?.[0].variant?.images?.[0] ?? '#'}
                            alt="Product image"
                            style={{ objectFit: 'contain' }}
                          />
                        </div>
                        <div className="flex grow items-start justify-between overflow-hidden">
                          <div className="max-w-full overflow-hidden">
                            <span
                              className="block max-w-full truncate font-primaryThin text-14 leading-normal"
                              data-testid={`not-added-item-name-${index}`}
                            >
                              {p?.name}
                            </span>
                            <span
                              className="mt-12 block font-primaryThin text-14 leading-normal"
                              data-testid={`not-added-item-size-${index}`}
                            >
                              {p.lineItems?.map((li: any) => `Size: ${li.variant?.attributes?.size.key}`)?.join(', ')}
                            </span>
                          </div>
                        </div>
                      </div>
                    </>
                  );
                })}
            </div>

            <div className="mt-18 flex w-full flex-col gap-16 md:mt-36 md:flex-row-reverse" data-testid="actions">
              <div className="md:flex-1">
                <Link link="/cart" onClick={hide} data-testid="cart">
                  {/* testid-ignore: nested in the cart link, which carries the testid */}
                  <button data-component-group="button" className="w-full border-2 border-primary-regular bg-secondary-regular py-8 font-secondaryBold text-16 uppercase text-primary-regular transition hover:bg-btn-primary-hover">
                    {formatCartMessage({ id: 'cart.go', defaultMessage: 'Go to Cart' })}
                  </button>
                </Link>
              </div>
              {/* testid-ignore: wrapper of the continue-shopping button, which carries the testid */}
              <div onClick={hide} className="md:flex-1">
                <button
                  data-testid="continue-shopping"
                  data-component-group="button"
                  className="w-full border-2 border-primary-regular bg-site py-8 font-secondaryBold text-16 uppercase transition hover:bg-btn-secondary-hover"
                >
                  {formatCartMessage({ id: 'continue.shopping', defaultMessage: 'Continue Shopping' })}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Transition>
      <AddToCartOverlayContext.Provider value={{ show, hide }}>{children}</AddToCartOverlayContext.Provider>
    </>
  );
};

export default AddToCartOverlayProvider;

export const useAddToCartOverlay = () => useContext(AddToCartOverlayContext);
